<template>
  <div v-if="sichtbar" class="testumgebung-banner" role="status" data-test="testumgebung-banner">
    <span class="testumgebung-banner-text">{{ kennzeichen }}</span>
  </div>
</template>

<script setup lang="ts">
// ─────────────────────────────────────────────────────────────────────────────
// components/TestumgebungBanner.vue — „man sieht es der Oberflaeche an,
// in welchem System sie laeuft" (T-M32.7, REQ-OPS-061).
//
// ── WARUM ES DIESEN STREIFEN GIBT, und er ist kein Schmuck ─────────────────
// Produktion und Testinstanz sind Zeichen fuer Zeichen dieselbe Anwendung. Ohne
// ein sichtbares Kennzeichen loescht frueher oder spaeter jemand in der
// Produktion, was er im Test loeschen wollte — die Ausgestaltung ist Sache des
// Umsetzers, die SICHTBARKEIT ist es nicht (T-M32.7 Kriterium 4).
//
// ── DIE BILDUNGSREGEL LIEGT NICHT HIER ─────────────────────────────────────
// Sie steht in `utils/umgebung.ts` und wird dort begruendet. Diese Datei
// stellt dar; sie entscheidet nicht.
//
// ── DER TEXT LAEUFT UEBER `vue-i18n` (REQ-OV-002) ──────────────────────────
// Das Wort „TESTSYSTEM" ist benutzergerichtet und uebersetzt; die PROJEKT-ID
// ist ein technischer Name und steht in jeder Sprache gleich.
//
// ── DIE VORLAGE TRAEGT KEINEN KOMMENTAR, und das ist Absicht ───────────────
// Ein `<!-- … -->` im Vorlagenteil wird MITGELIEFERT und steht danach im
// erzeugten Markup des Produktionsbaus — dort, wo diese Komponente gerade
// NICHTS hinterlassen soll. `role="status"` statt `role="alert"`: der Streifen
// ist ein Dauerzustand und keine Meldung, die eine Vorlesehilfe unterbrechen
// soll.
// ─────────────────────────────────────────────────────────────────────────────

import { computed } from 'vue';
import { useI18n }  from 'vue-i18n';
import { istTestumgebung, istEmulatorlauf, umgebungsProjektId } from '@/utils/umgebung';

const { t } = useI18n();

/**
 * Beim RENDERN gelesen, nicht beim Laden des Moduls: der Bau ersetzt
 * `import.meta.env` durch ein Objekt, und ein Beleg kann es stellvertreten.
 */
const sichtbar  = computed(() => istTestumgebung());
const projektId = computed(() => umgebungsProjektId());

/**
 * „TESTSYSTEM · <projektId>" — und am Emulator zusaetzlich der Grund.
 *
 * Der Emulatorfall braucht den Zusatz, weil die Projekt-ID dort `printerque-prod`
 * lautet (sie ist bei einem Emulatorlauf nur die Audience der ID-Token,
 * REQ-OPS-064 (a)). „TESTSYSTEM · printerque-prod" allein waere ein
 * Widerspruch auf dem Bildschirm; mit dem Zusatz ist es eine Aussage.
 */
const kennzeichen = computed(() =>
  istEmulatorlauf()
    ? t('umgebung.testBannerEmulator', { projektId: projektId.value })
    : t('umgebung.testBanner',         { projektId: projektId.value }),
);
</script>

<style scoped>
/* ── Der Streifen ────────────────────────────────────────────────────────────
   Fest am oberen Rand und ueber allem: die Seitenleiste liegt bei z-index 100
   und ist selbst `position: fixed`. Die Hoehe steht als `--testkennzeichen-h`
   in `App.vue` `:root` — dort schiebt dieselbe Groesse den Rahmen nach unten,
   damit der Streifen nichts verdeckt.

   Die Farbe ist die Warnfarbe des BESTEHENDEN Designsystems (`--color-pending`,
   Payfix-Palette in `App.vue`) — kein neuer Farbwert und kein UI-Framework. */
.testumgebung-banner {
  position:        fixed;
  top:    0;
  left:   0;
  right:  0;
  height:          var(--testkennzeichen-h);
  z-index:         2000;
  display:         flex;
  align-items:     center;
  justify-content: center;
  background:      var(--color-pending);
  color:           var(--text-primary);
  box-shadow:      0 1px 4px rgba(0, 0, 0, 0.18);
  pointer-events:  none;   /* verdeckt keine Bedienung, auch nicht am Rand */
  user-select:     none;
}

.testumgebung-banner-text {
  font-family:    var(--font-sans);
  font-size:      12px;
  font-weight:    700;
  letter-spacing: 0.09em;
  line-height:    1;
  white-space:    nowrap;
  overflow:       hidden;
  text-overflow:  ellipsis;
  padding:        0 12px;
}
</style>
